import React from 'react';
import { Box, Typography, Tooltip, alpha } from '@mui/material';
import {
  Notifications,
  TrendingUp,
  Info,
  CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// ── Stat tile ─────────────────────────────────
const StatTile = ({ label, value, sub, color, Icon }) => (
  <Box sx={{
    flex: 1, minWidth: 0,
    position: 'relative', overflow: 'hidden',
    borderRadius: 2,
    border: `1px solid ${alpha(color, 0.15)}`,
    bgcolor: '#fff',
    p: '12px 14px',
    transition: 'all 0.2s',
    '&:hover': {
      borderColor: alpha(color, 0.35),
      boxShadow: `0 2px 12px ${alpha(color, 0.1)}`,
      transform: 'translateY(-1px)',
    },
  }}>
    <Box sx={{
      position: 'absolute', top: 0, right: 0,
      width: 60, height: 60,
      background: `radial-gradient(circle at top right, ${alpha(color, 0.06)} 0%, transparent 70%)`,
    }} />
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.5, flexShrink: 0,
        background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 2px 6px ${alpha(color, 0.3)}`,
      }}>
        <Icon sx={{ fontSize: 18, color: '#fff' }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94a3b8', lineHeight: 1, mb: 0.4 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color, lineHeight: 1, fontFamily: 'monospace' }}>
          {value}
        </Typography>
        {sub && <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8', mt: 0.3, lineHeight: 1.3 }}>{sub}</Typography>}
      </Box>
    </Box>
  </Box>
);

const AlertsStatsCards = ({ stats, days, alerts, formatTimeAgo, getAlertTypeLabel }) => {
  const { t } = useTranslation();
  if (!stats) return null;

  const total = stats.total || 0;
  const high = stats.by_priority?.HIGH || 0;
  const medium = stats.by_priority?.MEDIUM || 0;
  const low = stats.by_priority?.LOW || 0;

  // Top types for tooltip
  const topTypes = stats.by_type
    ? Object.entries(stats.by_type).sort(([, a], [, b]) => b - a).slice(0, 5)
    : [];

  const typesTooltip = topTypes.length > 0 ? (
    <Box sx={{ minWidth: 180 }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#fff', mb: 0.5 }}>
        {t('alerts.stats.topAlertTypes')}
      </Typography>
      {topTypes.map(([type, count]) => (
        <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, py: 0.2 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1' }}>{getAlertTypeLabel(type)}</Typography>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#fff', fontFamily: 'monospace' }}>{count}</Typography>
        </Box>
      ))}
    </Box>
  ) : '';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
      <Tooltip title={typesTooltip} arrow placement="bottom">
        <Box>
          <StatTile
            label={t('alerts.stats.totalAlerts')}
            value={total}
            sub={t('alerts.stats.lastDays', { days })}
            color="#667eea"
            Icon={Notifications}
          />
        </Box>
      </Tooltip>

      <StatTile
        label={t('alerts.stats.highPriority')}
        value={high}
        sub={total > 0 ? t('alerts.stats.shareOfTotal', { percent: ((high / total) * 100).toFixed(0) }) : ''}
        color="#10b981"
        Icon={TrendingUp}
      />

      <StatTile
        label={t('alerts.stats.mediumPriority')}
        value={medium}
        sub={total > 0 ? t('alerts.stats.shareOfTotal', { percent: ((medium / total) * 100).toFixed(0) }) : ''}
        color="#f59e0b"
        Icon={Info}
      />

      <StatTile
        label={t('alerts.stats.lowPriority')}
        value={low}
        sub={total > 0 ? t('alerts.stats.shareOfTotal', { percent: ((low / total) * 100).toFixed(0) }) : ''}
        color="#3b82f6"
        Icon={CheckCircle}
      />
    </Box>
  );
};

export default AlertsStatsCards;
